import { fromJS } from "immutable";
import { REFRAME_LAYOUT } from "../constants";
import { randomString } from "./uuid";


export const getOriginalAspectRatio = (
    oWidth,
    oHeight,
    cWidth,
    cHeight,
    videoWidth,
    videoHeight,
    reframeX,
    reframeY,
    reframeW,
    itemX,
    itemY,
    layout
) => {

    const convertPosition = (x, y, frameWidth, frameHeight, targetWidth, targetHeight) => ({
        x: (x / frameWidth) * targetWidth,
        y: (y / frameHeight) * targetHeight,
    });

    const targetWidth = videoWidth;
    const targetHeight = videoHeight;

    const result = convertPosition(
        reframeX,
        reframeY,
        videoWidth,
        videoHeight,
        targetWidth,
        targetHeight
    );

    const currentWidth = cWidth || 450;
    const currentHeight = cHeight || 800;

    // =========================================================
    // 🔥 STEP 1: SCALE (object-fit: cover style)
    // =========================================================
    const scaleX = currentWidth / targetWidth;
    const scaleY = currentHeight / targetHeight;

    // Cover container (no empty space)
    const BASE_SCALE = Math.max(scaleX, scaleY);

    // 🔥 tweak this value
    const ZOOM_MAP = {
        FILL: 1,
        THREE: 1.65,
        FOUR: 1.85,
        SPLIT: 1.35,
    };

    const ZOOM_FACTOR = ZOOM_MAP[layout] || 1;

    const finalScale = BASE_SCALE * ZOOM_FACTOR;

    const scaledWidth = targetWidth * finalScale;
    const scaledHeight = targetHeight * finalScale;

    // =========================================================
    // 🔥 STEP 2: CENTER PERSON
    // =========================================================
    const personCenterX = result.x + (reframeW || 0) / 2;
    const personCenterY = result.y;

    const xDiff = currentWidth / 2 - personCenterX * finalScale;
    const yDiff = currentHeight / 2 - personCenterY * finalScale;

    // =========================================================
    // 🔥 STEP 3: SAFE BOUNDS (light clamp, no jumps)
    // =========================================================
    const minX = currentWidth - scaledWidth;
    const maxX = 0;

    const minY = currentHeight - scaledHeight;
    const maxY = 0;

    const finalX = Math.min(maxX, Math.max(minX, xDiff));
    const finalY = Math.min(maxY, Math.max(minY, yDiff));

    // =========================================================
    // 🔥 FILL layout (keep your original behavior)
    // =========================================================
    if (layout === "FILL") {
        return {
            x: finalX / currentWidth,
            y: 0,
            width: scaledWidth / currentWidth,
            height: 1,
        };
    }

    return {
        x: finalX / currentWidth,
        y: finalY / currentHeight,
        width: scaledWidth / currentWidth,
        height: scaledHeight / currentHeight,
    };
};

const processLayout = (coords, personKey, layoutType, projectWidth = 450, projectHeight = 800) => {
    const index = Number(personKey?.split("_")[1]) - 1;
    const box = REFRAME_LAYOUT[layoutType]?.boxes?.[index];

    if (!box) {
        return { coords, cWidth: undefined, reframeHeight: undefined, reframeWidth: undefined };
    }

    // Scale box dimensions to match project dimensions
    const scaleX = projectWidth / 450;
    const scaleY = projectHeight / 800;

    const scaledBox = {
        x: box.x * scaleX,
        y: box.y * scaleY,
        width: box.width * scaleX,
        height: box.height * scaleY,
    };

    coords = coords.set("wx", scaledBox.x).set("wp", scaledBox.y);

    return {
        coords,
        cWidth: scaledBox.width,
        reframeHeight: scaledBox.height,
        reframeWidth: scaledBox.width,
        cHeight: scaledBox.height,
    };
};


const buildFrameData = ({ scene, item, aiReframe, sceneIndex, sceneLength, projectWidth, projectHeight, startTime, endTime, type }) => {
    if (!scene || !item) return [];
    let personKeys = Object.keys(scene.get("coordinates").toJS());
    if (scene.get("layout") === "FIT" || scene.get("layout") === "FILL") {
        personKeys = personKeys.slice(0, 1);
    }
    const videoWidthVal = scene.get("frame_width") || aiReframe?.get("video_width") || item.get("pwidth") || 1920;
    const videoHeightVal = scene.get("frame_height") || aiReframe?.get("video_height") || item.get("pheight") || 1080;

    return personKeys.flatMap((personKey) => {
        const personCoordinates = scene.getIn(["coordinates", personKey]);
        if (!personCoordinates || personCoordinates.size === 0) return [];

        if (type === "shorts" && startTime >= scene.get("end_time") || type === "shorts" && endTime <= scene.get("start_time")) {
            return [];
        }
        if (
            (startTime && endTime && scene.get("end_time") <= startTime) ||
            (!(startTime && endTime) && scene.get("start_time") >= item.get("videoEnd"))
        ) {
            return [];
        }

        let coords = personCoordinates.get(0);

        let scaledWidth = projectWidth;
        let scaledHeight = (item.get("pheight") / item.get("pwidth")) * scaledWidth;

        if (scaledHeight < projectHeight) {
            scaledHeight = projectHeight;
            scaledWidth = (item.get("pwidth") / item.get("pheight")) * scaledHeight;
        }

        const videoWidth = scaledWidth > videoWidthVal ? scaledWidth : videoWidthVal;
        const videoHeight = scaledHeight > videoHeightVal ? scaledHeight : videoHeightVal;
        const aspectRatio = videoWidth / videoHeight;

        const oHeight = videoHeight;
        const oWidth = oHeight * aspectRatio;

        let reframeWidth = projectWidth;
        let reframeHeight = projectHeight;
        let cWidth = projectWidth;
        let cHeight = projectHeight;
        const layoutData = processLayout(coords, personKey, scene.get("layout"), projectWidth, projectHeight);
        
        if (!layoutData.cWidth && scene.get("layout") !== "FIT" && scene.get("layout") !== "FILL") {
            return [];
        }

        coords = layoutData.coords;

        cWidth = layoutData.cWidth || cWidth;
        cHeight = layoutData.cHeight || cHeight;
        reframeHeight = layoutData.reframeHeight || reframeHeight;
        reframeWidth = layoutData.reframeWidth || reframeWidth;

        const itemWidth = scaledWidth;
        const itemHeight = scaledHeight;
        const newX = (coords.get("x") / videoWidthVal) * itemWidth;
        const newY = (coords.get("y") / videoHeightVal) * itemHeight;
        const updateX = scaledWidth > videoWidthVal ? newX : coords.get("x");
        const updateY = scaledHeight > videoHeightVal ? newY : coords.get("y");

        // coordW: person bounding-box width from first coordinate, scaled if item is larger than source video
        const coordW = coords.get("w") || 0;
        const updateW = scaledWidth > videoWidthVal
            ? (coordW / videoWidthVal) * itemWidth
            : coordW;

        const originalResult = getOriginalAspectRatio(
            oWidth,
            oHeight,
            cWidth,
            cHeight,
            videoWidth,
            videoHeight,
            updateX,
            updateY,
            updateW,
            item.get("x"),
            item.get("y"),
            scene.get("layout")
        );

        const updatedScene = scene.toJS();
        updatedScene.sceneIndex = sceneIndex;
        updatedScene.sceneLength = sceneLength;
        updatedScene.personKey = personKey;
        updatedScene.movable = personCoordinates.size > 1;
        // Store actual video dimensions from API so aireframehandler never falls back to hardcoded 1280/720
        updatedScene.frame_width = videoWidthVal || updatedScene.frame_width;
        updatedScene.frame_height = videoHeightVal || updatedScene.frame_height;
        delete updatedScene.coordinates;

        const referenceVideoStart = startTime || item.get("videoStart") || 0;
        const referenceVideoEnd = endTime || item.get("videoEnd");

        const videoStartTime = Math.max(scene.get("start_time"), referenceVideoStart);
        const videoEndTime = Math.min(scene.get("end_time"), referenceVideoEnd);

        if (videoStartTime >= videoEndTime) {
            return [];
        }
        const sceneOpacity = (scene.get("layout") !== "SPLIT") &&
            (scene.get("layout") !== "THREE") &&
            (scene.get("layout") !== "FOUR") &&
            (scene.get("layout") !== "SCREENSHARE") &&
            (scene.get("layout") !== "GAMEPLAY") &&
            (scene.get("layout") !== "FIT") &&
            coords.get("opacity") !== undefined
            ? coords.get("opacity")
            : 1

        const speed = item.get("speed") || 1;
        let timelineStart = item.get("enterStart") + (videoStartTime - referenceVideoStart) / speed;
        const timelineEnd = item.get("enterStart") + (videoEndTime - referenceVideoStart) / speed;

        const isFirstScene = sceneIndex === 0;
        if (isFirstScene && timelineStart > item.get("enterStart") && (timelineStart - item.get("enterStart")) < 0.1) {
            timelineStart = item.get("enterStart");
        }

        return {
            x: coords.get("wx") || 0,
            y: coords.get("wp") || 0,
            width: reframeWidth || 450,
            height: reframeHeight,
            layout: scene.get("layout"),
            sceneId: scene.get("scene_id"),
            enterStart: timelineStart,
            enterEnd: timelineStart,
            exitStart: timelineEnd,
            exitEnd: timelineEnd,
            // enterEffectName: "aiReframe",
            videoStart: videoStartTime,
            videoEnd: videoEndTime,
            opacity: sceneOpacity,
            original: scene.get("layout") === "FIT" ? undefined : originalResult,
            volume: 0,
            personKey,
            sceneData: updatedScene,
        };
    });
};


export function createNewScene(reframeData, item, originalData, sceneIndex, sceneLength, projectWidth, projectHeight) {
    const toUpdate = [];
    const aiReframe = fromJS(originalData);
    const scene = fromJS(reframeData);
    const frameData = buildFrameData({
        scene,
        item,
        aiReframe,
        sceneIndex,
        sceneLength,
        projectWidth,
        projectHeight
    });

    // Process each frame and create new workspace items
    frameData.forEach((frame, index) => {
        const itemId = item.get("dropId") || item.get("originalId") || item.get("id");
        const newItemId = `scene${frame.sceneId}-newreframe${itemId}-${frame.layout}${index}`;

        const newItem = item.merge({
            id: newItemId,
            drop: newItemId,
            dropId: itemId,
            originalId: item.get("id"),
            isReframe: false,
            x: frame.x,
            y: frame.y,
            width: frame.width,
            height: frame.height,
            enterStart: frame.enterStart,
            enterEnd: frame.enterEnd,
            videoStart: frame.videoStart,
            exitStart: frame.exitStart,
            exitEnd: frame.exitEnd,
            videoEnd: frame.videoEnd,
            original: frame.original,
            opacity: frame.opacity,
            volume: frame.volume,
            layout: frame.layout,
            sceneId: `scene${frame.sceneId}`,
            sceneData: frame.sceneData,
            personKey: frame.personKey,
            // enterEffectName: frame.enterEffectName,
            isCropped: !!frame.original,
        });

        toUpdate.push({
            container: "workspaceItems",
            id: newItemId,
            newItemData: newItem.toJS(),
            isAdd: true,
            isLibraryDrop: true,
            isReframe: true
        });
    });

    return toUpdate;
}

export function createNewReframe(item, reframeData, projectWidth, projectHeight, startTime, endTime, type) {
    if (!item.get("id")) return item;
    const toUpdate = [];
    const aiReframe = fromJS(reframeData);
    if (aiReframe?.get("scenes")?.size > 0) {
        const scenes = aiReframe.get("scenes");
        const itemWithoutAiReframe = item.delete("aiReframe");
        const sortedScenes = [...scenes].sort((a, b) => a.start_time - b.start_time);

        const frameData = sortedScenes.flatMap((scene, index) =>
            buildFrameData({
                scene,
                item,
                aiReframe,
                sceneIndex: index,
                sceneLength: scenes.size,
                projectWidth,
                projectHeight,
                startTime,
                endTime,
                type
            })
        );

        frameData.forEach((frame, index) => {
            const newItemId = `scene${frame.sceneId}-${randomString("reframe")}${index}`;
            const newItem = itemWithoutAiReframe.merge({
                id: newItemId,
                drop: newItemId,
                originalId: item.get("id"),
                x: frame.x,
                y: frame.y,
                width: frame.width,
                height: frame.height,
                enterStart: frame.enterStart,
                enterEnd: frame.enterEnd,
                videoStart: frame.videoStart,
                exitStart: frame.exitStart,
                exitEnd: frame.exitEnd,
                videoEnd: frame.videoEnd,
                original: frame.original,
                opacity: frame.opacity,
                volume: frame.volume,
                isReframe: false,
                layout: frame.layout,
                sceneId: `scene${frame.sceneId}`,
                sceneData: frame.sceneData,
                personKey: frame.personKey,
                // enterEffectName: frame.enterEffectName,
                isCropped: !!frame.original,
            });
            if (item.get("id") !== newItemId) {
                toUpdate.push({
                    container: "workspaceItems",
                    id: newItemId,
                    newItemData: newItem.toJS(),
                    isAdd: true,
                    isLibraryDrop: true,
                    isReframe: true,
                });
            }
        });
    }
    return toUpdate;
}


/**
 *
 * @param {Immutable.Map} item          - Workspace item that has sceneData (aiReframe clip)
 * @param {number}        absoluteTime  - Current playhead (seconds, absolute timeline time)
 * @param {*}             reframeData   - projectDetails.reframeData — may be an Immutable Map
 *                                        keyed by assetId or a plain-JS object.
 * @param {*}             appReframeData- app.reframeData fallback (single-asset, plain-JS or Immutable)
 * @param {number}        zoomFactor    - Current workspace zoom factor
 * @param {number}        projectWidth  - Project canvas width (viewport width)
 * @returns {number|null}               - translateX in CSS pixels, or null if not applicable
 */
export const getAiReframeStaticX = (item, absoluteTime, reframeData, appReframeData, zoomFactor, projectWidth) => {
    if (!item || !item.get("sceneData")) return null;

    const enterStart = item.get("enterStart");
    const exitEnd = item.get("exitEnd");
    if (absoluteTime < enterStart || absoluteTime > exitEnd) return null;

    const assetId = item.get("assetId");
    if (!assetId) return null;

    // ── 1. Resolve scenes array from reframeData (mirrors shouldCallAiReframeHandler) ──
    let scenesRaw = null;

    if (reframeData) {
        const rd = reframeData.toJS ? reframeData.toJS() : reframeData;
        if (rd[assetId] && rd[assetId].scenes) {
            scenesRaw = rd[assetId].scenes;
        }
    }

    if (!scenesRaw && appReframeData) {
        const ad = appReframeData.toJS ? appReframeData.toJS() : appReframeData;
        if (ad[assetId] && ad[assetId].scenes) {
            scenesRaw = ad[assetId].scenes;
        } else if (ad.scenes && (ad.assetId === assetId || !ad.assetId)) {
            scenesRaw = ad.scenes;
        }
    }

    if (!scenesRaw || scenesRaw.length === 0) return null;

    // ── 2. Find the matching scene ──
    const sceneId = item.getIn(["sceneData", "scene_id"]);
    const scene = scenesRaw.find((s) => s.scene_id === sceneId);
    if (!scene) return null;

    // ── 3. Resolve person coordinates ──
    const personKey = item.get("personKey") || item.getIn(["sceneData", "personKey"]);
    const allCoords = scene.coordinates || {};
    const keys = Object.keys(allCoords);
    const fallbackKey = keys.length > 0 ? keys[keys.length - 1] : null;
    const personCoords = (personKey && allCoords[personKey])
        ? allCoords[personKey]
        : (fallbackKey ? allCoords[fallbackKey] : null);

    if (!Array.isArray(personCoords) || personCoords.length === 0) return null;

    // ── 4. Geometry (mirrors aireframehandler exactly) ──
    const containerWidth = item.get("width") || projectWidth;
    const original = item.get("original");
    const origX = original ? (original.get ? original.get("x") : original.x) : 0;
    const origWidth = original ? (original.get ? original.get("width") : original.width) : 1;

    const videoOffsetX = -(origX * containerWidth);
    const videoRenderedWidth = origWidth * containerWidth;
    const frameWidth = scene.frame_width || 1280;
    const pixelScale = videoRenderedWidth / frameWidth;

    // Hard limits: clamp translateX so the video never pans past its own edges.
    const maxTweenX = videoOffsetX * zoomFactor;
    const minTweenX = (containerWidth - videoRenderedWidth + videoOffsetX) * zoomFactor;

    const calcTweenX = (coordX, coordW = 0) => {
        const personCentre = (coordX + coordW / 2) * pixelScale;
        const raw = (containerWidth / 2 - personCentre + videoOffsetX) * zoomFactor;
        return Math.min(maxTweenX, Math.max(minTweenX, raw));
    };
    const PAN_INSET = 4 * zoomFactor; // px of guaranteed over-cover at each edge
    const canInset = (maxTweenX - minTweenX) > PAN_INSET * 2;
    const relMax = canInset ? maxTweenX - PAN_INSET : (maxTweenX + minTweenX) / 2;
    const relMin = canInset ? minTweenX + PAN_INSET : (maxTweenX + minTweenX) / 2;
    const clampRel = (rel) => Math.min(relMax, Math.max(relMin, rel));

    // ── 5. Find the correct pan position at absoluteTime ──
    const speed = item.get("speed") || 1;
    const skippedSceneTime = (item.get("videoStart") || 0) - (scene.start_time || 0);
    const relativeTime = (absoluteTime - enterStart) * speed + skippedSceneTime;
    const sceneDuration = (scene.end_time || 0) - (scene.start_time || 0);

    const sortedCoords = [...personCoords].sort((a, b) => a.t - b.t);

    // Initial X — derived from first valid coord (mirrors aireframehandler initialX logic)
    const firstCoord = sortedCoords.find((c) => (c.e ?? sceneDuration) - c.t > 0) ?? sortedCoords[0];
    if (!firstCoord) return null;

    const baseX = calcTweenX(firstCoord.x, firstCoord.w || 0);
    let prevX = baseX;

    for (let i = 0; i < sortedCoords.length; i++) {
        const coord = sortedCoords[i];
        const nextCoord = sortedCoords[i + 1];
        const endTime = coord.e !== undefined
            ? coord.e
            : (nextCoord ? nextCoord.t : sceneDuration);
        const duration = endTime - coord.t;

        // eslint-disable-next-line no-continue
        if (duration <= 0) continue;

        const targetX = calcTweenX(coord.x, coord.w || 0);

        if (relativeTime >= coord.t && relativeTime <= endTime) {
            // Linear interpolation within this segment (matches GSAP Linear.easeNone)
            const progress = duration > 0 ? (relativeTime - coord.t) / duration : 0;
            const absX = prevX + (targetX - prevX) * progress;
            return clampRel(absX - baseX);
        }

        prevX = targetX;
    }

    return clampRel(prevX - baseX);
};


/**
 * Splits the selected aiReframe scene at the specified split time.
 *
 * @param {object} aiReframe - The aiReframe object containing scenes.
 * @param {number} splitTime - The time at which to split the scene.
 * @returns {object} - Updated aiReframe object with new scenes.
 */

export const splitAiReframeScene = (aiReframe, splitTime) => {
    if (!aiReframe || !aiReframe.scenes) return aiReframe;

    const updatedScenes = [];

    aiReframe.scenes.forEach((scene) => {
        const {
            id,
            start_time: startTime,
            end_time: endTime,
            layout,
            coordinates,
        } = scene;

        if (splitTime > startTime && splitTime < endTime) {
            const randomId = randomString();

            // Split into two parts
            const firstPart = {
                id: `${id}`,
                start_time: startTime,
                end_time: splitTime,
                layout,
                coordinates,
            };

            const secondPart = {
                id: `${id}${randomId}`,
                start_time: splitTime,
                end_time: endTime,
                layout,
                coordinates,
            };

            updatedScenes.push(firstPart, secondPart);
        } else {
            updatedScenes.push(scene);
        }
    });

    // Sort by start_time
    updatedScenes.sort((a, b) => a.start_time - b.start_time);

    return fromJS({
        ...aiReframe,
        scenes: updatedScenes,
    });
};